import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import {
  TooltipContent,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
} from '#/shared/ui/tooltip'

/** Phone number that copies itself to the clipboard on click, with a tooltip
 *  hint ("Click to copy") that flips to "Number copied" briefly after a copy. */
export function CopyableNumber({
  number,
  className,
}: {
  number: string
  className?: string
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    },
    [],
  )

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(number)
    } catch {
      return
    }
    setCopied(true)
    setOpen(true)
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setCopied(false), 2000)
  }

  return (
    <TooltipProvider delayDuration={200}>
      <TooltipRoot open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => void handleCopy()}
            className={cn(
              'cursor-pointer transition-colors hover:text-gray-900',
              className,
            )}
          >
            {number}
          </button>
        </TooltipTrigger>
        <TooltipContent>
          {copied ? t('phonePanel.numberCopied') : t('phonePanel.copyNumber')}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  )
}
